/*
  Плашка над шапкой карточки - судебная работа управляющей компании (Б1).

  Вариант Б1 выбран владельцем 05.10.2026 по макетам: одна фраза и одна кнопка.
  Решения владельца (конституция, §3.3.2):
  - «взыскано по ...» - зелёная: месяцы УК в заявление не пойдут, делать
    ничего не нужно, но знать надо - заявление начнётся позже первого месяца долга;
  - дело без месяцев («в суде 16.04») - жёлтая: счёт в очереди как обычно, но
    до подачи человек отмечает месяцы, иначе вторая подача за те же месяцы;
  - «придержан» - жёлтая: заказчик просил подождать, снимает отметку человек.

  С 06.10.2026 дела показываются не плашкой на каждое, а таблицей «Дела по счёту»
  (вариант В2, `AccountCases.jsx`): там же наше заявление и не предъявленные
  месяцы. Плашками остались отметка «придержан» и примечание.

  Фраза приходит с сервера - те же слова, что у экрана загрузки реестра. Кнопки
  видит только тот, кто ведёт дело (`manage_stages`); остальные видят фразу.
  Плашки нет вовсе, пока заказчик о счёте ничего не сказал.
*/

import { useState } from 'react'

import AccountCases from './AccountCases.jsx'
import { Notice } from './Common.jsx'
import { CaseMonthsForm, ClearHoldForm, FilingReturnForm } from './CustomerWorkForms.jsx'
import useCustomerWork from './useCustomerWork.js'

/*
  `children` - блок сумм карточки. Он встаёт между плашками и таблицей дел
  (указание владельца 06.10.2026: сначала «Долг к взысканию», потом «Дела по
  счёту»), а плашки «придержан» и «примечание» остаются над суммами - их
  читают раньше. Суммы показываются и пока плашка грузится или не загрузилась:
  карточка не ждёт дел.

  🔴 Суммы стоят на ОДНОМ месте разметки во всех состояниях - загрузка, отказ,
  готово. Ранний возврат «пока грузится - только суммы» ставил их на другое
  место, React пересоздавал блок, и раскрытое «Почему система решила так»
  сворачивалось само, когда приходили дела (поймано тестом вкладок 06.10.2026).
*/
function HoldNotice({ hold, canAct, onClear }) {
  return (
    <Notice kind="warn" wide>
      <div className="notice__what">
        Счёт придержан по реестру управляющей компании: «{hold.reason}». В очередь к подаче он
        не идёт.
      </div>
      <div className="notice__do">
        Разберитесь и снимите отметку - счёт вернётся в очередь.{' '}
        {canAct ? (
          <button className="button button--quiet" type="button" onClick={onClear}>
            Снять отметку
          </button>
        ) : null}
      </div>
    </Notice>
  )
}

export default function CustomerWork({
  accountId,
  canAct,
  readonly,
  onChanged,
  onGo,
  version,
  children,
}) {
  const { work, cases, error, clearHold, setMonths, returnFiling } = useCustomerWork(
    accountId,
    onChanged,
    version,
  )
  const [clearing, setClearing] = useState(false)
  const [editing, setEditing] = useState(null)
  const [returning, setReturning] = useState(null)

  return (
    <>
      {error ? <Notice error={error} wide /> : null}
      {work?.hold ? (
        <HoldNotice hold={work.hold} canAct={canAct} onClear={() => setClearing(true)} />
      ) : null}
      {work?.registry_note ? (
        <Notice kind="warn" wide>
          Примечание управляющей компании в реестре: «{work.registry_note}».
        </Notice>
      ) : null}
      {children}
      {/* Дела - таблицей В2 (06.10.2026) вместо плашки на каждое дело. Окно
          отметки месяцев прежнее: ему нужно само дело УК, а не строка таблицы. */}
      {work ? (
        <AccountCases
          table={cases}
          canAct={canAct}
          onEdit={(row) => setEditing(work.cases.find((item) => item.id === row.id) ?? null)}
          onGo={onGo}
          readonly={readonly}
          onReturn={setReturning}
        />
      ) : null}
      {/* Окно - пока отметка есть: успешное снятие обновляет плашку раньше,
          чем окно закрывается, и без этого условия окно падало бы на пустой
          отметке (поймано тестом 05.10.2026). */}
      {clearing && work?.hold ? (
        <ClearHoldForm hold={work.hold} onClear={clearHold} onClose={() => setClearing(false)} />
      ) : null}
      {editing ? (
        <CaseMonthsForm item={editing} onSave={setMonths} onClose={() => setEditing(null)} />
      ) : null}
      {returning ? (
        <FilingReturnForm item={returning} onSave={returnFiling} onClose={() => setReturning(null)} />
      ) : null}
    </>
  )
}
